import clsx from 'clsx';

/** App-wide page canvas — clean white (not sand). */
export const pageCanvasClass = 'bg-white dark:bg-surface';

/** Padded section on the page canvas (checkout, cart, account-style pages). */
export const pageCanvasSectionClass = clsx(pageCanvasClass, 'pb-12 pt-4');

/** Shared max-width + horizontal padding for listing hub pages. */
export const listingPageContainerClass = 'mx-auto max-w-listing px-4';

/** Bottom padding for listing hub pages (page background comes from ShellLayout). */
export const listingPageSurfaceClass = 'pb-12';

/** Breadcrumb nav on listing surface — even space on both sides of ›. */
export const listingBreadcrumbNavClass =
  'flex flex-wrap items-center gap-x-0 gap-y-1.5 text-xs text-muted';

export const listingBreadcrumbLinkClass =
  'font-medium text-default hover:text-brand-900 hover:underline dark:hover:text-brand-400';

/** Padding on the sep (not flex gap) so › reads evenly between crumbs. */
export const listingBreadcrumbSepClass = 'mx-1.5 inline-block select-none text-faint';

export const listingBreadcrumbCurrentClass = 'font-medium text-default';

/** Panel borders used on listing hub cards, sidebars, and hub bar. */
export const listingPanelBorderClass = 'border border-default';

/** Hover surface for filter rows and secondary controls. */
export const listingHoverRowClass = 'hover:bg-surface-sunken';

/** Muted pill / count background in filter sidebars. */
export const listingCountPillClass =
  'rounded-full bg-surface-sunken px-1.5 text-[10px] text-muted';

/** Horizontal category / sector tab row on listing pages. */
export const listingCategoryStripShellClass =
  'overflow-x-auto border-b border-default bg-white dark:bg-surface';

/** Tab link styling inside listingCategoryStripShellClass rows. */
export function listingCategoryTabClass(active: boolean): string {
  return clsx(
    'relative shrink-0 px-2.5 py-2 text-sm transition',
    active
      ? 'font-semibold text-brand-800 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-brand-700 dark:text-brand-300'
      : 'font-medium text-muted hover:text-brand-700 dark:hover:text-brand-400',
  );
}

/**
 * Three-column listing hub grid: filters | results | ads.
 * Filter column matches pre–Phase 3 width (240–280px).
 */
export const listingPageGridClass =
  'grid grid-cols-1 gap-6 lg:grid-cols-[minmax(240px,280px)_minmax(0,1fr)] lg:items-start xl:grid-cols-[minmax(240px,280px)_minmax(0,1fr)_15rem]';

export const listingPageGridSpanClass = 'col-span-full';

/** Hub bar row inside the listing grid — spans filters through ads columns. */
export const listingPageHubShellClass = clsx(
  'col-span-full rounded-lg bg-surface-raised px-4 py-3 sm:px-5 sm:py-4',
  listingPanelBorderClass,
);

/** Left filter column — card on listing surface. */
export const listingPageFilterAsideClass = clsx(
  'hidden lg:block lg:self-start rounded-2xl bg-surface-raised p-4',
  listingPanelBorderClass,
);

/** Main results column — aligned with hub bar container edges. */
export const listingPageResultsPanelClass = clsx(
  'min-w-0 rounded-lg bg-surface-raised p-4 sm:p-5',
  listingPanelBorderClass,
);

/** Business directory card grid. */
export const listingBusinessGridClass =
  'grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3';

/** Property listing cards — compact browse grid in the results panel. */
export const listingPropertyGridClass =
  'grid gap-3 sm:grid-cols-2 lg:grid-cols-3';

/** Product cards — responsive browse grid in the shop results panel. */
export const listingProductGridClass =
  'grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
